/**
 * JS Object compilation for the page runtime (ADR-0044 J1).
 *
 * A page's JS Object is the single home for authored CLIENT logic. Its module
 * is the Appsmith shape — `export default { fn(args){…}, … }` — and its sync
 * functions are callable from `{{ }}` bindings as `{{ <name>.fn(args) }}`.
 *
 * Unlike the `{{ }}` binding interpreter (`pageExpr.ts`, a restricted AST
 * evaluator that NEVER uses `new Function`), a JS Object IS real JavaScript:
 * ADR-0043's keystone is "the client host is native JS — the user's OWN browser
 * over their OWN already-authorized data, no sandbox needed". The JS Object is
 * operator-authored (same trust level as the page config) and reaches NO data
 * by itself (J1 functions are PURE functions of their arguments — data arrives
 * as a binding argument, e.g. `{{ Utils.fullName($item) }}`); RLS is still
 * re-forced server-side on every Query. So compiling it with `new Function`
 * here does not widen the END-USER's authority — it runs the app author's code
 * in the user's browser, like any web app's bundled JS.
 *
 * Web-Worker isolation (ADR-0043's stated direction) is a deferred hardening;
 * J1 compiles on the main thread. The namespace is TAGGED
 * (`asJsObjectNamespace`) so the binding interpreter will dispatch a call onto
 * it — an untagged plain object stays method-call-rejected (the safe default).
 */
import { asJsObjectNamespace } from "./pageExpr";
import type { LibraryContext } from "./pageLibraries";

export interface JsObjectDef {
  name: string;
  code: string;
}

export interface CompiledJsObjects {
  /** name → tagged namespace of the JS Object's exported functions. */
  namespaces: Record<string, Record<string, unknown>>;
  /** Per-object compile errors (name → message), for the builder to surface. */
  errors: Record<string, string>;
}

/** Strip a leading `export default` (the Appsmith module shape) so the body is a
 *  bare object literal we can wrap in `return (...)`. Tolerates whitespace and a
 *  trailing semicolon. A body without `export default` is returned as-is. */
function stripExportDefault(code: string): string {
  const trimmed = code.trim().replace(/;\s*$/, "");
  const m = /^export\s+default\s*/.exec(trimmed);
  return m ? trimmed.slice(m[0].length) : trimmed;
}

/**
 * Compile ONE JS Object module to a tagged namespace, or return an error.
 * Empty code → an empty (valid) namespace. The module must evaluate to an
 * object; its function values become the callable members.
 */
export function compileJsObject(
  code: string,
  lib?: LibraryContext,
): { ns: Record<string, unknown> } | { error: string } {
  const body = stripExportDefault(code);
  if (!body) return { ns: asJsObjectNamespace({}) };
  try {
    // ADR-0046 — inject the app's external libraries (lodash `_`, dayjs, …) as
    // `new Function` PARAMS so the JS Object body can reference them by global,
    // plus a `require(name)` shim resolving a library by PACKAGE name. The
    // returned object's methods are closures that capture these params.
    const names = lib ? Object.keys(lib.globals) : [];
    const values = lib ? names.map((n) => lib.globals[n]) : [];
    const requireFn = lib ? lib.require : () => undefined;
    // Real JS (the ADR-0043 client host). Strict mode: no implicit globals.
    const factory = new Function(...names, "require", `"use strict"; return (${body});`) as (
      ...a: unknown[]
    ) => unknown;
    const obj = factory(...values, requireFn);
    if (obj == null || typeof obj !== "object") {
      return { error: "a JS Object must export an object (export default { ... })" };
    }
    return { ns: asJsObjectNamespace(obj as Record<string, unknown>) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Compile a page's JS Objects to a `{ name → namespace }` map (+ an error map).
 * A failed module contributes an empty namespace so a binding `{{ Bad.fn() }}`
 * degrades to "no such function" rather than crashing the whole page; the error
 * is surfaced to the builder via `errors`.
 */
export function compileJsObjects(
  defs: readonly JsObjectDef[] | undefined,
  lib?: LibraryContext,
): CompiledJsObjects {
  const namespaces: Record<string, Record<string, unknown>> = {};
  const errors: Record<string, string> = {};
  for (const def of defs ?? []) {
    if (!def || typeof def.name !== "string" || !def.name) continue;
    const res = compileJsObject(typeof def.code === "string" ? def.code : "", lib);
    if ("error" in res) {
      errors[def.name] = res.error;
      namespaces[def.name] = asJsObjectNamespace({});
    } else {
      namespaces[def.name] = res.ns;
    }
  }
  return { namespaces, errors };
}

// Compiling a JS Object module uses `new Function` — so cache by the (stable)
// `js_objects` array reference from the loaded page AST, keeping the binding
// scope build (called every render) cheap. Both the live runtime and the
// builder preview share this cache. The cache is KEYED on (defs, libContext):
// libraries load async, so the same `defs` is compiled first with no libs then
// with the loaded set — a single-level cache would pin the stale no-libs build.
const _cache = new WeakMap<object, WeakMap<object, CompiledJsObjects>>();
const _EMPTY: CompiledJsObjects = { namespaces: {}, errors: {} };
const _NO_LIB_TOKEN: object = {};

/** `compileJsObjects` memoized on the (array identity, library context). A
 *  missing/empty list returns a shared empty result (no compile). Pass a STABLE
 *  `lib` object per loaded set (see `buildLibraryContext`) so it's a sound key. */
export function compileJsObjectsCached(
  defs: readonly JsObjectDef[] | undefined,
  lib?: LibraryContext,
): CompiledJsObjects {
  if (!defs || defs.length === 0) return _EMPTY;
  let inner = _cache.get(defs as object);
  if (!inner) {
    inner = new WeakMap();
    _cache.set(defs as object, inner);
  }
  const token: object = lib ?? _NO_LIB_TOKEN;
  const cached = inner.get(token);
  if (cached) return cached;
  const compiled = compileJsObjects(defs, lib);
  inner.set(token, compiled);
  return compiled;
}
